import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDatePicker from 'react-datepicker-v2';
import classnames from 'classnames';
import moment from 'moment';
import _ from 'lodash';

import CustomTimePickerInput from './custom_time_picker_input';

import './brzw_time_picker.scss';

const CLASS_NAME = 'brzw-time-picker';

const BrzwTimePicker = ({
  calendarClassName,
  children,
  className,
  customInput,
  disabled,
  error,
  icon,
  id,
  inputClassName,
  label,
  maxTime,
  minTime,
  onBlur,
  onChange,
  placeholderText,
  selected,
  theme,
  timeFormat,
  timeIntervals,
  withClearAction,
}) => {
  const customInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [highlightedTime, setHighlightedTime] = useState(selected);
  const maxPickerTime =
    maxTime ||
    moment()
      .endOf('day')
      .toDate();

  const onCalendarClose = () => {
    _.invoke(customInputRef, 'current.blur');
  };

  const onChangeDateByInput = date => {
    onChange(date);
    _.invoke(datePickerRef, 'current.setOpen', false);
  };

  const handleHighlightedTimeClass = time => {
    if (!highlightedTime) return '';
    return moment(time).isSame(highlightedTime) ? `${CLASS_NAME}__item--highlighted` : '';
  };

  useEffect(() => {
    setHighlightedTime(selected);
  }, [selected]);

  return (
    <div className={classnames('brzw-time-picker', className)} ref={wrapperRef}>
      <ReactDatePicker
        calendarClassName={calendarClassName}
        className={inputClassName}
        customInput={
          customInput || (
            <CustomTimePickerInput
              error={error}
              highlightedTime={highlightedTime}
              icon={icon}
              id={id}
              inputRef={customInputRef}
              label={label}
              minTime={minTime}
              onChangeDate={onChangeDateByInput}
              placeholder={placeholderText}
              selectedDate={selected}
              setHighlightedTime={setHighlightedTime}
              theme={theme}
              withClearAction={withClearAction}
              wrapperRef={wrapperRef}
            />
          )
        }
        customInputRef={customInputRef}
        dateFormat={timeFormat}
        fixedHeight
        maxDate={minTime && maxPickerTime}
        maxTime={minTime && maxPickerTime}
        minTime={minTime}
        onBlur={onBlur}
        onCalendarClose={onCalendarClose}
        onChange={onChange}
        openToDate={highlightedTime}
        placeholderText={placeholderText}
        readOnly={disabled}
        ref={datePickerRef}
        selected={selected}
        showTimeSelect
        showTimeSelectOnly
        timeClassName={handleHighlightedTimeClass}
        timeFormat={timeFormat}
        timeIntervals={timeIntervals}
      >
        {children}
      </ReactDatePicker>
    </div>
  );
};

BrzwTimePicker.propTypes = {
  calendarClassName: PropTypes.string,
  children: PropTypes.shape({}),
  className: PropTypes.string,
  customInput: PropTypes.shape({}),
  disabled: PropTypes.bool,
  error: PropTypes.string,
  icon: PropTypes.string,
  id: PropTypes.string,
  inputClassName: PropTypes.string,
  label: PropTypes.string,
  maxTime: PropTypes.shape({}),
  minTime: PropTypes.shape({}),
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  placeholderText: PropTypes.string,
  selected: PropTypes.shape({}),
  theme: PropTypes.string,
  timeFormat: PropTypes.string,
  timeIntervals: PropTypes.number,
  withClearAction: PropTypes.bool,
};

BrzwTimePicker.defaultProps = {
  calendarClassName: '',
  children: null,
  className: '',
  customInput: null,
  icon: 'time',
  inputClassName: '',
  label: '',
  placeholderText: 'Add time',
  timeFormat: 'h:mma',
  timeIntervals: 15,
  withClearAction: true,
};

export default BrzwTimePicker;
